import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";

const PlayerCtx = createContext(null);
export const usePlayer = () => useContext(PlayerCtx);

const FADE_SECS = 3;

export function PlayerProvider({ children }) {
  const audio = useRef(null);
  const volumeRef = useRef(0.85);
  const trackRef = useRef(null);
  const [track, setTrack] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(0.85);

  useEffect(() => {
    const a = new Audio();
    a.preload = "metadata";
    audio.current = a;
    const onTime = () => {
      const t = a.currentTime;
      const limit = trackRef.current?.previewSeconds || 60;
      setTime(t);
      if (t >= limit) {
        a.pause();
        a.currentTime = 0;
        setPlaying(false);
      } else if (t >= limit - FADE_SECS) {
        a.volume = volumeRef.current * (1 - (t - (limit - FADE_SECS)) / FADE_SECS);
      } else {
        a.volume = volumeRef.current;
      }
    };
    const onMeta = () => setDuration(a.duration);
    const onEnd = () => setPlaying(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnd);
    return () => {
      a.pause();
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnd);
    };
  }, []);

  const setVolume = (v) => {
    volumeRef.current = v;
    const limit = trackRef.current?.previewSeconds || 60;
    if (audio.current && audio.current.currentTime < limit - FADE_SECS) audio.current.volume = v;
    setVolumeState(v);
  };

  const toggle = useCallback((t) => {
    const a = audio.current;
    if (!a) return;
    if (t && t.id !== track?.id) {
      a.src = t.preview;
      a.currentTime = 0;
      a.volume = volumeRef.current;
      trackRef.current = t;
      setTrack(t);
      setTime(0);
      a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
      return;
    }
    if (a.paused) {
      const limit = trackRef.current?.previewSeconds || 60;
      if (a.currentTime >= limit) a.currentTime = 0;
      a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    } else {
      a.pause();
      setPlaying(false);
    }
  }, [track]);

  const seek = (v) => {
    const limit = trackRef.current?.previewSeconds || 60;
    if (audio.current) {
      audio.current.currentTime = Math.min(v, limit);
      setTime(Math.min(v, limit));
    }
  };

  const close = () => {
    audio.current?.pause();
    setPlaying(false);
    setTrack(null);
  };

  return (
    <PlayerCtx.Provider value={{ track, playing, time, duration, volume, setVolume, toggle, seek, close }}>
      {children}
    </PlayerCtx.Provider>
  );
}
